import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Coins, Sword, Clock, Trophy, Gem } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { fetchLeaderboard } from "@/lib/api";

const CATEGORIES = [
  { key: "richest", label: "Richest", icon: Coins },
  { key: "pvp", label: "Top PvP", icon: Sword },
  { key: "playtime", label: "Playtime", icon: Clock },
  { key: "events", label: "Event Winners", icon: Trophy },
  { key: "coins", label: "DC Coins", icon: Gem },
];

const medalColor = (rank) => rank === 1 ? "#FFBF00" : rank === 2 ? "#C0C0C0" : rank === 3 ? "#CD7F32" : null;

function Row({ entry }) {
  const color = medalColor(entry.rank);
  return (
    <div
      data-testid={`leaderboard-row-${entry.rank}`}
      className={`flex items-center gap-4 p-4 rounded-xl glass hover-lift ${color ? "border-[#FFBF00]/30" : ""}`}
    >
      <div
        className="w-12 h-12 rounded-lg grid place-items-center font-hero text-xl shrink-0"
        style={{ background: color ? `${color}22` : "rgba(255,255,255,0.05)", color: color || "#fff" }}
      >
        #{entry.rank}
      </div>
      <img src={`https://mc-heads.net/avatar/${entry.username}/64`} alt={entry.username} className="w-11 h-11 rounded-md" loading="lazy" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{entry.username}</div>
        <div className="text-[11px] uppercase tracking-widest text-white/50">Rank {entry.rank}</div>
      </div>
      <div className="text-right">
        <div className="font-hero text-2xl text-[#FFBF00]">{entry.value.toLocaleString("en-IN")}</div>
        <div className="text-[10px] uppercase tracking-widest text-white/50">{entry.unit}</div>
      </div>
    </div>
  );
}

export default function Leaderboards() {
  const [data, setData] = useState({});
  useEffect(() => {
    CATEGORIES.forEach(c => fetchLeaderboard(c.key).then((d) => setData(prev => ({ ...prev, [c.key]: d }))).catch(() => {}));
  }, []);

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-leaderboards">
      <div className="max-w-5xl mx-auto">
        <SectionHeader eyebrow="Rankings" title="Legends of LostDC" subtitle="The best of the best — updated every season." />
        <Tabs defaultValue="richest">
          <TabsList className="bg-black/60 border border-white/10 backdrop-blur-xl w-full flex-wrap h-auto p-1">
            {CATEGORIES.map(c => (
              <TabsTrigger
                key={c.key}
                value={c.key}
                data-testid={`lb-tab-${c.key}`}
                className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black text-white/70 uppercase text-xs tracking-widest px-4 py-2"
              >
                <c.icon size={14} className="mr-2" /> {c.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {CATEGORIES.map(c => (
            <TabsContent key={c.key} value={c.key} className="mt-8 space-y-3">
              {(data[c.key]?.entries || []).map((e) => <Row key={e.rank} entry={e} />)}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
